import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Helper function to generate a reference number
const generateRefNo = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const random = String(Math.floor(Math.random() * 10000)).padStart(4, '0');
  
  return `REF-${year}${month}${day}-${hours}${minutes}${seconds}-${random}`;
};

interface EmailCommunication {
  id: number;
  date: string;
  created_at?: string;
  sent_to: string;
  subject?: string;
  message: string;
  employee: string;
  delivery_status: 'sent' | 'delivered' | 'failed' | 'pending';
  ref_no: string;
  forward?: string;
}

interface ClientEmail {
  id: number;
  email: string;
  is_primary: boolean;
  email_type: string;
}

interface PersonOfInterest {
  id: number;
  email: string;
  name: string;
  surname: string;
}

interface InstructionDocument {
  id: number;
  file_name: string;
  original_name: string;
  file_path: string;
  description: string;
  created_at: string;
  file_size: number;
  document_type: string;
}

interface ClientInstructionEmailTabProps {
  clientId: string;
  instructionId: string;
  instructionRef: string;
  clientName?: string;
  policyNumber?: string;
  user?: {
    id?: number;
    username?: string;
    name?: string;
    email?: string;
  };
}

const ClientInstructionEmailTab: React.FC<ClientInstructionEmailTabProps> = ({ 
  clientId,
  instructionId,
  instructionRef,
  clientName = '',
  policyNumber = '',
  user
}) => {
  const [emails, setEmails] = useState<EmailCommunication[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailCommunication | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  const [emailOptions, setEmailOptions] = useState<{ value: string; label: string; type: string }[]>([]);
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);
  
  // Documents state - specifically for instruction documents
  const [instructionDocuments, setInstructionDocuments] = useState<InstructionDocument[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);

  const [formData, setFormData] = useState({
    sent_to: '',
    create_recipient: '',
    subject: `[${instructionRef}] `,
    message: '',
    selectedDocuments: [] as number[],
    instruction_id: instructionId
  });

  const [recipients, setRecipients] = useState<string[]>([]);

  const [selectedForForward, setSelectedForForward] = useState<number[]>([]);
  const [loggedInUser, setLoggedInUser] = useState<string>('System');
  const [forwardAttachments, setForwardAttachments] = useState<File[]>([]);
  const [forwardAttachmentData, setForwardAttachmentData] = useState<{
  sent_to: string;
  date: string;
  subject: string;
  message: string;
} | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{current: number; total: number} | null>(null);

  const MAX_DOCUMENTS = 5;

  // Fetch documents whenever instructionId changes
  useEffect(() => {
    if (instructionId) {
      fetchInstructionDocuments();
    }
  }, [instructionId]);

  // Fetch emails
  useEffect(() => {
    fetchEmails();
    fetchEmailOptions();
  }, [clientId]);

  // Get logged-in user
  useEffect(() => {
    if (user) {
      const userName = user.username || user.name || user.email;
      if (userName) {
        setLoggedInUser(userName);
        return;
      }
    }

    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const userName = userData.username || userData.name || userData.email || 'System';
        setLoggedInUser(userName);
      } catch {
        setLoggedInUser('System');
      }
    }
  }, [user]);

  // Set subject with instruction reference
  useEffect(() => {
    if (instructionRef) {
      setFormData(prev => ({
        ...prev,
        subject: `[${instructionRef}] `,
        message: `Client Name: ${clientName}\nPolicy Number: ${policyNumber}\n\n`
      }));
    }
  }, [instructionRef, clientName, policyNumber]);

  const fetchEmails = async () => {
  try {
    setIsLoading(true);
    setError(null);
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication required');
      setIsLoading(false);
      return;
    }

    const url = `${process.env.REACT_APP_API_URL}/api/email/communications/${clientId}?instruction_id=${instructionId}`;

    const response = await axios.get(
      url,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const emailData = response.data.data || response.data || [];
    
    // Debug: Log all emails with their subjects
    console.log('📧 Fetched emails from database:', emailData.length);
    emailData.forEach((email: any, index: number) => {
      console.log(`  Email ${index + 1}: ID=${email.id}, Subject="${email.subject}", Ref=${email.ref_no}`);
      // Ensure subject is a string
      if (email.subject === undefined || email.subject === null) {
        console.warn(`  ⚠️ Email ${email.id} has no subject!`);
      }
    });
    
    setEmails(emailData);
  } catch (err) {
    console.error('Error fetching email communications:', err);
    setError('Failed to load email communications');
    setEmails([]);
  } finally {
    setIsLoading(false);
  }
};

  const fetchInstructionDocuments = async () => {
    if (!instructionId) return;
    
    setIsLoadingDocuments(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No token found for fetching instruction documents');
        setIsLoadingDocuments(false);
        return;
      }

      console.log(`📄 Fetching instruction documents for ID: ${instructionId}`);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/instruction-documents?instructionId=${instructionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const documents = Array.isArray(response.data) ? response.data : response.data.data || [];
      console.log(`✅ Found ${documents.length} instruction documents`);
      
      // Map documents to match expected format
      const mappedDocuments = documents.map((doc: any) => ({
        id: doc.id,
        file_name: doc.file_name || doc.original_name || 'document.pdf',
        original_name: doc.original_name || doc.file_name || 'document.pdf',
        file_path: doc.file_path || '',
        description: doc.description || '',
        created_at: doc.created_at || new Date().toISOString(),
        file_size: doc.file_size || 0,
        document_type: doc.document_type || 'instruction'
      }));
      
      setInstructionDocuments(mappedDocuments);
    } catch (err) {
      console.error('Error fetching instruction documents:', err);
      setInstructionDocuments([]);
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const fetchEmailOptions = async () => {
    try {
      setIsLoadingEmails(true);
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No token found for fetching email options');
        setIsLoadingEmails(false);
        return;
      }

      const options: { value: string; label: string; type: string }[] = [];

      // 1. Fetch client emails
      try {
        const clientEmailsRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/client-emails/${clientId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const clientEmails = clientEmailsRes.data.data || [];
        clientEmails.forEach((email: ClientEmail) => {
          if (email.email) {
            options.push({
              value: email.email,
              label: `${email.email} (Client)`,
              type: 'Client'
            });
          }
        });
      } catch (err) {
        console.warn('Could not fetch client emails:', err);
      }

      // 2. Fetch persons of interest
      try {
        const poiResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/persons-of-interest?clientId=${clientId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const persons = Array.isArray(poiResponse.data) ? poiResponse.data : poiResponse.data.data || [];
        persons.forEach((person: PersonOfInterest) => {
          if (person.email) {
            options.push({
              value: person.email,
              label: `${person.email} (Person Of Interest)`,
              type: 'POI'
            });
          }
        });
      } catch (err) {
        console.warn('Could not fetch persons of interest:', err);
      }

      // Remove duplicates
      const seen = new Set<string>();
      const uniqueOptions = options.filter(option => {
        const key = option.value.toLowerCase().trim();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      setEmailOptions(uniqueOptions);
    } catch (err) {
      console.error('Error fetching email options:', err);
      setError('Failed to load email options');
    } finally {
      setIsLoadingEmails(false);
    }
  };

  const handleDocumentToggle = (docId: number) => {
    setFormData(prev => {
      const selected = prev.selectedDocuments.includes(docId);
      if (selected) {
        return {
          ...prev,
          selectedDocuments: prev.selectedDocuments.filter(id => id !== docId)
        };
      } else {
        return {
          ...prev,
          selectedDocuments: [...prev.selectedDocuments, docId]
        };
      }
    });
  };

  const handleAddRecipient = () => {
    const email = formData.sent_to.trim();
    if (!email) {
      setError('Please select an email address from the dropdown');
      return;
    }
    if (recipients.includes(email)) {
      setError('This email address is already added');
      return;
    }
    setRecipients([...recipients, email]);
    setFormData(prev => ({ ...prev, sent_to: '' }));
    setError(null);
  };

  const handleAddCreateRecipient = () => {
    const email = formData.create_recipient.trim();
    if (!email) {
      setError('Please enter an email address');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (recipients.includes(email)) {
      setError('This email address is already added');
      return;
    }
    setRecipients([...recipients, email]);
    setFormData(prev => ({ ...prev, create_recipient: '' }));
    setError(null);
  };

  const handleRemoveRecipient = (email: string) => {
    setRecipients(recipients.filter(r => r !== email));
  };

  const handleForwardToggle = (emailId: number) => {
  // Log the email being selected
  const email = emails.find(e => e.id === emailId);
  console.log('📧 Email selected for forward:', email);
  console.log('📧 Subject in email:', email?.subject);
  console.log('📧 All keys in email:', email ? Object.keys(email) : 'none');
  
  setSelectedForForward(prev => {
    if (prev.includes(emailId)) {
      return prev.filter(id => id !== emailId);
    } else {
      return [...prev, emailId];
    }
  });
};

  const handleViewEmail = (email: EmailCommunication) => {
    setSelectedEmail(email);
    setShowPreviewModal(true);
  };

  const handleForwardSelected = () => {
  if (selectedForForward.length === 0) {
    setError('Please select at least one email to forward');
    return;
  }
  
  const selectedEmails = emails.filter(email => selectedForForward.includes(email.id));
  if (selectedEmails.length > 0) {
    const firstEmail = selectedEmails[0];
    
    console.log('📧 Full email object for forwarding:', JSON.stringify(firstEmail, null, 2));
    
    // Get the original subject - USE THE EMAIL'S SUBJECT FIELD FROM THE DATABASE
let originalSubject = '';

// FIRST: Use the email's subject field from the database (this contains the full subject)
if (firstEmail.subject && firstEmail.subject.trim() !== '') {
  originalSubject = firstEmail.subject.trim();
  console.log('📧 Using email.subject from database:', originalSubject);
} else {
  console.warn('📧 firstEmail.subject is empty or undefined:', firstEmail.subject);
  console.warn('📧 Full email object:', JSON.stringify(firstEmail, null, 2));
  console.warn('📧 All keys in firstEmail:', Object.keys(firstEmail));
}

// SECOND: Try to extract from message if subject is missing
if (!originalSubject && firstEmail.message) {
  // Check if the message starts with the subject pattern
  const messageLines = firstEmail.message.split('\n');
  // Look for Subject: in the message content
  const subjectMatch = firstEmail.message.match(/Subject:\s*(.*?)(?:\n|$)/i);
  if (subjectMatch) {
    originalSubject = subjectMatch[1].trim();
    console.log('📧 Extracted subject from message:', originalSubject);
  }
  
  // Also check if the message starts with [INS-25]
  if (!originalSubject) {
    const refMatch = firstEmail.message.match(/^\[INS-\d+\]/);
    if (refMatch) {
      originalSubject = refMatch[0];
      console.log('📧 Extracted ref from message:', originalSubject);
    }
  }
}

// THIRD: If still empty, use instruction ref
if (!originalSubject && instructionRef) {
  originalSubject = `[${instructionRef}]`;
  console.log('📧 Using instruction ref as subject:', originalSubject);
}

// SECOND: If subject is empty, try to extract from message content
if (!originalSubject && firstEmail.message) {
  // Look for Subject: in the message
  const subjectMatch = firstEmail.message.match(/Subject:\s*(.*?)(?:\n|$)/i);
  if (subjectMatch) {
    originalSubject = subjectMatch[1].trim();
    console.log('📧 Extracted subject from message:', originalSubject);
  }
}

// THIRD: If still empty, use instruction ref
if (!originalSubject && instructionRef) {
  originalSubject = `[${instructionRef}]`;
  console.log('📧 Using instruction ref as subject:', originalSubject);
}
    
    // If still empty, use default
    if (!originalSubject) {
      originalSubject = 'Forwarded Email';
      console.log('📧 Using default subject:', originalSubject);
    }
    
    // Create the forwarded subject
    const subjectPrefix = `FWD: ${originalSubject}`;
    const forwardRefNo = generateRefNo();
    
    console.log('📧 Final forwarded subject:', subjectPrefix);
    
    // Extract client name and policy number from the original message
    let clientName = '';
    let policyNumber = '';
    let originalMessage = firstEmail.message || '';
    
    // Try to extract client name and policy number from the message
    const nameMatch = originalMessage.match(/Client Name:\s*(.*?)(?:\n|$)/);
    const policyMatch = originalMessage.match(/Policy Number:\s*(.*?)(?:\n|$)/);
    
    if (nameMatch) {
      clientName = nameMatch[1].trim();
    }
    if (policyMatch) {
      policyNumber = policyMatch[1].trim();
    }
    
    // Build the forwarded message - ONLY client info
    let forwardedMessage = '';
    
    // Add client info
    if (clientName || policyNumber) {
      forwardedMessage += 'Client Name: ' + clientName + '\n';
      forwardedMessage += 'Policy Number: ' + policyNumber;
    }
    
    // Trim any extra whitespace
    forwardedMessage = forwardedMessage.trim();
    
    // Create the email content for the attachment with the FULL original subject
    const emailContent = `
========== Forwarded Message ========
From: ${firstEmail.sent_to}
Date: ${new Date(firstEmail.date).toLocaleString()}
Subject: ${originalSubject}
----------------------------------------

${firstEmail.message || ''}
========================================
    `.trim();
    
    // Create the attachment
    const blob = new Blob([emailContent], { type: 'text/plain' });
    const file = new File([blob], `Forwarded_Email_${forwardRefNo}.txt`, { type: 'text/plain' });
    
    // Store original email data for later attachment generation
    setForwardAttachmentData({
      sent_to: firstEmail.sent_to,
      date: firstEmail.date,
      subject: originalSubject, // Store the FULL original subject
      message: firstEmail.message || ''
    });
    
    // Set the form data with the forwarded message content
    setForwardAttachments([]); // Clear any existing attachments
    setFormData(prev => ({
      ...prev,
      subject: subjectPrefix,
      message: forwardedMessage,
      sent_to: '',
      create_recipient: ''
    }));
    setRecipients([]);
    setShowCreateModal(true);
    setError(null);
    setSelectedForForward([]);
  }
};

  const handleCreateEmail = async (e: React.FormEvent) => {
  e.preventDefault();
  
  if (isSubmitting) {
    console.warn('⚠️ Already submitting, ignoring duplicate click');
    return;
  }
  
  const hasSelectedRecipient = formData.sent_to.trim() !== '';
  const hasAddedRecipients = recipients.length > 0;
  
  if (!hasSelectedRecipient && !hasAddedRecipients) {
    setError('Please select a recipient from "Send To" or add recipients using "Create Recipient"');
    return;
  }
  if (!formData.subject.trim()) {
    setError('Subject is required');
    return;
  }
  if (!formData.message.trim()) {
    setError('Message is required');
    return;
  }

  const selectedDocs = instructionDocuments.filter(doc => 
    formData.selectedDocuments.includes(doc.id)
  );

  if (selectedDocs.length > MAX_DOCUMENTS) {
    setError(`Too many documents selected. Maximum ${MAX_DOCUMENTS} documents allowed.`);
    return;
  }

  const generatedRefNo = generateRefNo();

  try {
    setIsSubmitting(true);
    setError(null);
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication required');
      setIsSubmitting(false);
      return;
    }

    const validDocs = selectedDocs.filter(doc => doc && doc.id && (doc.file_name || doc.original_name));
    if (validDocs.length === 0) {
      console.log('📎 No valid documents to attach, proceeding without attachments');
    }

    let allRecipients = [...recipients];
    if (formData.sent_to.trim() && !allRecipients.includes(formData.sent_to.trim())) {
      allRecipients.push(formData.sent_to.trim());
    }

    const formDataPayload = new FormData();
    formDataPayload.append('to', JSON.stringify(allRecipients));
    formDataPayload.append('subject', formData.subject.trim());
    formDataPayload.append('message', formData.message.trim());
    formDataPayload.append('client_id', clientId);
    formDataPayload.append('ref_no', generatedRefNo);
    formDataPayload.append('employee', loggedInUser);
    formDataPayload.append('document_ids', JSON.stringify(formData.selectedDocuments));
    formDataPayload.append('documents', JSON.stringify([]));
    formDataPayload.append('instruction_id', instructionId);

    // Add logging
console.log('📧 Sending email with subject:', formData.subject.trim());
console.log('📧 Subject value:', JSON.stringify(formData.subject.trim()));
console.log('📧 Subject length:', formData.subject.trim().length);

    const totalFiles = validDocs.length + forwardAttachments.length;
    setUploadProgress({ current: 0, total: totalFiles });

    let successfullyDownloadedDocs: any[] = [];

    for (let i = 0; i < validDocs.length; i++) {
      const doc = validDocs[i];
      
      if (!doc || !doc.id) {
        console.warn(`⚠️ Invalid document at index ${i}, skipping...`);
        setUploadProgress(prev => prev ? { ...prev, current: prev.current + 1 } : null);
        continue;
      }

      console.log(`📎 Processing document ${i + 1}/${validDocs.length}: ${doc.original_name || doc.file_name} (ID: ${doc.id})`);

      if (!doc.file_path || doc.file_path.trim() === '') {
        console.warn(`⚠️ Document ${doc.id} has no file path, skipping...`);
        setUploadProgress(prev => prev ? { ...prev, current: prev.current + 1 } : null);
        continue;
      }

      try {
        const downloadUrl = `${process.env.REACT_APP_API_URL}/api/instruction-documents/download/${doc.id}`;
        
        console.log(`📎 Downloading document ${doc.id} from: ${downloadUrl}`);

        const response = await axios.get(
          downloadUrl,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
            timeout: 30000,
            maxContentLength: Infinity,
            maxBodyLength: Infinity
          }
        );
        
        if (!response || !response.data) {
          console.warn(`⚠️ No data received for document ${doc.id}, skipping...`);
          setUploadProgress(prev => prev ? { ...prev, current: prev.current + 1 } : null);
          continue;
        }

        const fileBlob = response.data;
        
        if (!(fileBlob instanceof Blob) || fileBlob.size === 0) {
          console.warn(`⚠️ Invalid or empty file for document ${doc.id}, skipping...`);
          setUploadProgress(prev => prev ? { ...prev, current: prev.current + 1 } : null);
          continue;
        }

        const fileName = doc.original_name || doc.file_name || 'document.pdf';
        console.log(`✅ Downloaded: ${fileName} (${fileBlob.size} bytes)`);

        const MAX_FILE_SIZE = 25 * 1024 * 1024;
        if (fileBlob.size > MAX_FILE_SIZE) {
          console.warn(`⚠️ File ${fileName} is ${formatFileSize(fileBlob.size)}, exceeding limit, skipping...`);
          setUploadProgress(prev => prev ? { ...prev, current: prev.current + 1 } : null);
          continue;
        }

        if (fileName.toLowerCase().endsWith('.pdf')) {
          const reader = new FileReader();
          const signatureCheck = await new Promise<string>((resolve) => {
            reader.onload = (e) => {
              const result = e.target?.result as ArrayBuffer;
              const uint8Array = new Uint8Array(result);
              const signature = Array.from(uint8Array.slice(0, 5))
                .map(b => String.fromCharCode(b))
                .join('');
              resolve(signature);
            };
            reader.readAsArrayBuffer(fileBlob.slice(0, 10));
          });
          
          if (signatureCheck !== '%PDF-') {
            console.warn(`⚠️ File ${fileName} is not a valid PDF, skipping...`);
            setUploadProgress(prev => prev ? { ...prev, current: prev.current + 1 } : null);
            continue;
          }
        }

        formDataPayload.append('files', fileBlob, fileName);
        console.log(`✅ Added file to email: ${fileName}`);
        successfullyDownloadedDocs.push(doc);

      } catch (err: any) {
        if (err.response?.status === 404) {
          console.warn(`⚠️ Document ${doc.id} not found on server (404), skipping...`);
        } else if (err.response?.status === 500) {
          console.warn(`⚠️ Server error (500) for document ${doc.id}, skipping...`);
        } else if (err.code === 'ECONNABORTED') {
          console.warn(`⚠️ Download timeout for document ${doc.id}, skipping...`);
        } else {
          console.warn(`⚠️ Failed to download document ${doc.id}:`, err.message || 'Unknown error');
        }
      } finally {
        setUploadProgress(prev => prev ? { ...prev, current: prev.current + 1 } : null);
      }
    }

    if (successfullyDownloadedDocs.length > 0) {
      const documentsData = successfullyDownloadedDocs.map(doc => ({
        id: doc.id,
        file_name: doc.file_name,
        original_name: doc.original_name,
        file_path: doc.file_path
      }));
      formDataPayload.set('documents', JSON.stringify(documentsData));
      console.log(`✅ Added ${successfullyDownloadedDocs.length} document(s) to metadata`);
    } else {
      formDataPayload.set('documents', JSON.stringify([]));
      console.log('📎 No documents were successfully downloaded');
    }

    if (forwardAttachmentData) {
  // Use the ORIGINAL subject from forwardAttachmentData, NOT the formData subject
  const originalSubject = forwardAttachmentData.subject || 'No Subject';
  
  console.log('📧 Generating forwarded attachment with original subject:', originalSubject);
  
  const emailContent = `
========== Forwarded Message ========
From: ${forwardAttachmentData.sent_to}
Date: ${new Date(forwardAttachmentData.date).toLocaleString()}
Subject: ${originalSubject}
----------------------------------------

${forwardAttachmentData.message}
========================================
  `.trim();
  
  const blob = new Blob([emailContent], { type: 'text/plain' });
  const file = new File([blob], `Forwarded_Email_${generateRefNo()}.txt`, { type: 'text/plain' });
  
  formDataPayload.append('files', file, file.name);
  console.log(`✅ Added forwarded email attachment with original subject: ${originalSubject}`);
}

// Add any additional forward attachments (if any)
for (let i = 0; i < forwardAttachments.length; i++) {
  const file = forwardAttachments[i];
  formDataPayload.append('files', file, file.name);
  console.log(`✅ Added forward attachment: ${file.name}`);
  setUploadProgress(prev => prev ? { ...prev, current: prev.current + 1 } : null);
}

    console.log('📧 Sending email with FormData');
    console.log('📧 Recipients:', allRecipients);
    console.log('📧 Number of files:', formDataPayload.getAll('files').length);

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/email/send-formdata`,
      formDataPayload,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to send email');
    }

    const allRecipientsForDisplay = [...recipients];
    if (formData.sent_to.trim() && !allRecipientsForDisplay.includes(formData.sent_to.trim())) {
      allRecipientsForDisplay.push(formData.sent_to.trim());
    }

    const newEmail = {
  id: Date.now(),
  date: new Date().toISOString(),
  sent_to: allRecipientsForDisplay.join(', '),
  subject: formData.subject.trim(), // Add the subject!
  message: formData.message.trim(),
  employee: loggedInUser,
  delivery_status: 'sent' as EmailCommunication['delivery_status'],
  ref_no: generatedRefNo,
  forward: ''
};
setEmails([newEmail, ...emails]);

    setError(null);
resetForm();
setShowCreateModal(false);
setForwardAttachments([]);
setForwardAttachmentData(null); // Add this line
setUploadProgress(null);
setIsSubmitting(false); // CRITICAL: Reset submitting state

    alert(`✅ Email sent successfully to ${allRecipientsForDisplay.length} recipient(s)!`);

  } catch (err: any) {
    console.error('❌ Error sending email:', err);

    console.error('❌ Backend response:', {
      status: err.response?.status,
      data: err.response?.data,
      message: err.message,
    });

    console.error('❌ Backend error:', err.response?.data?.error);
    console.error('❌ Backend stack:', err.response?.data?.stack);

    const errorMessage =
      err.response?.data?.error ||
      err.response?.data?.message ||
      err.message ||
      'Failed to send email';

    setError(errorMessage);
    setIsSubmitting(false); // CRITICAL: Reset submitting state even on error

    if (err.code === 'ECONNABORTED') {
      alert(
        '⏱️ Request timed out. The email may still have been sent. Please check your sent folder.'
      );
    } else {
      alert(`❌ ${errorMessage}`);
    }
  }
};

  const resetForm = () => {
  setFormData({
    sent_to: '',
    create_recipient: '',
    subject: `[${instructionRef}] `,
    message: `Client Name: ${clientName}\nPolicy Number: ${policyNumber}\n\n`,
    selectedDocuments: [],
    instruction_id: instructionId
  });
  setSelectedForForward([]);
  setRecipients([]);
  setForwardAttachments([]);
  setForwardAttachmentData(null);
  setUploadProgress(null);
};

  const handleOpenEmailModal = () => {
  // Clear selected documents when opening modal
  setFormData(prev => ({
    ...prev,
    selectedDocuments: []
  }));
  setRecipients([]);
  setSelectedForForward([]);
  setForwardAttachments([]);
  setForwardAttachmentData(null);
  setShowCreateModal(true);
  setError(null);
};

  const handleCloseModal = () => {
  resetForm();
  setShowCreateModal(false);
  setError(null);
  setSelectedForForward([]);
  setForwardAttachments([]);
  setForwardAttachmentData(null);
  setUploadProgress(null);
  setIsSubmitting(false);
};

  const getStatusBadgeClass = (status: EmailCommunication['delivery_status']) => {
    const classes = {
      sent: 'bg-blue-100 text-blue-800',
      delivered: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    return classes[status] || classes.pending;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="text-gray-500">Loading email communications...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Instruction Email Communications</h3>
        <div className="flex gap-2">
          {selectedForForward.length > 0 && (
            <button
              onClick={handleForwardSelected}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Forward Selected ({selectedForForward.length})
            </button>
          )}
          <button
            onClick={handleOpenEmailModal}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Send Mail
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {emails.length === 0 ? (
        <div className="text-gray-500 text-center py-8">
          No email communications found for this instruction.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent To</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ref No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Forward</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {emails.map((email) => (
                <tr key={email.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(email.date).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {email.sent_to}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                    {email.message || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {email.employee}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(email.delivery_status)}`}>
                      {email.delivery_status.charAt(0).toUpperCase() + email.delivery_status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {email.ref_no || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <button
                      onClick={() => handleViewEmail(email)}
                      className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <input
                      type="checkbox"
                      checked={selectedForForward.includes(email.id)}
                      onChange={() => handleForwardToggle(email.id)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      title="Select this email to forward"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Email Preview Modal */}
      {showPreviewModal && selectedEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Email Preview</h3>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setSelectedEmail(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="bg-gray-50 p-4 rounded-md space-y-2">
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">From:</span>
                  <span className="text-sm text-gray-900 flex-1">{selectedEmail.sent_to}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Date:</span>
                  <span className="text-sm text-gray-900 flex-1">
                    {new Date(selectedEmail.date).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Subject:</span>
                  <span className="text-sm text-gray-900 flex-1 font-medium">
                    {selectedEmail.subject || '-'}
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Employee:</span>
                  <span className="text-sm text-gray-900 flex-1">{selectedEmail.employee}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Ref No:</span>
                  <span className="text-sm text-gray-900 flex-1 font-mono">{selectedEmail.ref_no || '-'}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Status:</span>
                  <span className="text-sm flex-1">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(selectedEmail.delivery_status)}`}>
                      {selectedEmail.delivery_status.charAt(0).toUpperCase() + selectedEmail.delivery_status.slice(1)}
                    </span>
                  </span>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Message:</h4>
                <div className="bg-white border border-gray-200 rounded-md p-4 whitespace-pre-wrap text-sm text-gray-800 min-h-[100px] max-h-[300px] overflow-y-auto">
                  {selectedEmail.message || '-'}
                </div>
              </div>
            </div>
            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setSelectedEmail(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Email Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Send Mail</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleCreateEmail} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Send To</label>
                <p className="text-xs text-gray-500 mb-2">
                  Select from existing contacts and click Add, or use "Create Recipient" below for a new email address
                </p>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <select
                      value={formData.sent_to}
                      onChange={(e) => setFormData({ ...formData, sent_to: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
                      disabled={isLoadingEmails}
                    >
                      <option value="">Select an email address...</option>
                      {emailOptions.length === 0 && !isLoadingEmails && (
                        <option value="" disabled>No email addresses found</option>
                      )}
                      {emailOptions.map((option, index) => (
                        <option key={index} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddRecipient}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 transition-colors whitespace-nowrap"
                  >
                    Add
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Create Recipient</label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <input
                      type="email"
                      value={formData.create_recipient}
                      onChange={(e) => setFormData({ ...formData, create_recipient: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter a new email address"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddCreateRecipient();
                        }
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAddCreateRecipient}
                    className="px-4 py-2 bg-green-500 text-white rounded-md text-sm font-medium hover:bg-green-600 transition-colors whitespace-nowrap"
                  >
                    Add
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Enter a new email address not listed above and click Add</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Recipients ({recipients.length})</label>
                {recipients.length === 0 ? (
                  <div className="p-3 border border-gray-200 rounded-md bg-gray-50 text-gray-400 text-sm">
                    No recipients added yet. Use "Send To" or "Create Recipient" above to add recipients.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 p-3 border border-gray-200 rounded-md bg-gray-50">
                    {recipients.map((email, index) => (
                      <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                        {email}
                        <button
                          type="button"
                          onClick={() => handleRemoveRecipient(email)}
                          className="ml-1 text-blue-600 hover:text-blue-800 focus:outline-none"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Email subject"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  placeholder="Email message content..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Files</label>
                {isLoadingDocuments ? (
                  <div className="text-gray-500 text-sm py-2">Loading documents...</div>
                ) : instructionDocuments.length === 0 ? (
                  <div className="text-gray-500 text-sm py-2">No documents found for this instruction.</div>
                ) : (
                  <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                    {instructionDocuments.map((doc) => (
                      <label key={doc.id} className="flex items-start space-x-3 py-2 hover:bg-gray-50 px-2 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.selectedDocuments.includes(doc.id)}
                          onChange={() => handleDocumentToggle(doc.id)}
                          className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {doc.original_name || doc.file_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {doc.document_type || 'Document'} • {formatFileSize(doc.file_size || 0)}
                            {doc.created_at && ` • ${new Date(doc.created_at).toLocaleDateString()}`}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                {formData.selectedDocuments.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.selectedDocuments.length} file(s) selected
                  </p>
                )}
              </div>

              {uploadProgress && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Uploading files...</span>
                    <span>{uploadProgress.current} of {uploadProgress.total}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    uploadProgress ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...` : 'Sending...'
                  ) : 'Send Mail'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientInstructionEmailTab;